"use client";

import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

interface AdvancedListSortableRowProps {
  id: string;
  children: React.ReactNode;
}

/**
 * Generic drag handle + row wrapper for AdvancedList's optional reorder mode.
 * Same mechanism as apps/hub's SimpleColumnManager ShownColumnsList (dnd-kit
 * sortable), generalized to wrap arbitrary item content instead of a column
 * row specifically.
 */
export function AdvancedListSortableRow({ id, children }: AdvancedListSortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      <button
        type="button"
        className="mt-2 touch-manipulation cursor-grab active:cursor-grabbing rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label="Reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
